<template>
  <div class="lesson-text-box">
    <VLessonPlaceholder v-if="isEmptyBlock" />
    
    <div v-else
      class="w-full h-full m-0 box-border transition-all relative overflow-hidden"
      :style="[
        CssStyleHelpers.borderRadius(fields.mainGroup?.borderRadius),
        CssStyleHelpers.background(
          fields.mainGroup?.backgroundColor,
          fields.mainGroup?.isShowColor
        ),
        CssStyleHelpers.margins(fields.mainGroup?.margins),
      ]"
    >
      <div
        class="w-full h-full flex main-group"
        :style="[
          CssStyleHelpers.flexDirection(true),
          CssStyleHelpers.paddings(fields.mainGroup?.paddings),
          {
            '--main-padding-top-mobile': `${fields.mainGroup?.paddingsMobile?.top ?? fields.mainGroup?.paddings?.top ?? 0}px`,
            '--main-padding-right-mobile': `${fields.mainGroup?.paddingsMobile?.right ?? fields.mainGroup?.paddings?.right ?? 0}px`,
            '--main-padding-bottom-mobile': `${fields.mainGroup?.paddingsMobile?.bottom ?? fields.mainGroup?.paddings?.bottom ?? 0}px`,
            '--main-padding-left-mobile': `${fields.mainGroup?.paddingsMobile?.left ?? fields.mainGroup?.paddings?.left ?? 0}px`,
          },
        ]"
      >
        <!-- Медиа контент -->
        <div
          v-if="fields.mainGroup?.isShowMedia"
          class="media-background w-full h-full absolute inset-0 z-0 overflow-hidden"
          :style="[CssStyleHelpers.borderRadius(fields.mainGroup?.borderRadius)]"
        >
          <MediaCard
            fill
            :media-type="fields.mainGroup?.mediaBackgroundType"
            :image-props="fields.mainGroup?.imageProps"
            :video-props="fields.mainGroup?.videoProps"
            :mask-props="fields.mainGroup?.maskProps"
            :object-fit="fields.mainGroup?.objectFit || 'cover'"
          />
        </div>

        <!-- Карточки -->
        <div
          v-if="fields.cardsGroup?.cardsCollection?.length > 0"
          class="w-full h-full overflow-hidden flex flex-col relative z-10"
          :style="[
            CssStyleHelpers.gap(fields.cardsGroup?.cardInnerSpacing),
          ]"
        >
          <DialogCard
            v-for="(card, index) in fields.cardsGroup?.cardsCollection"
            :key="index"
            :card="card"
            :cards-group="fields.cardsGroup"
            :inner-spacing="fields.cardsGroup?.cardTextPaddings"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, PropType } from "vue";
import { useDataFields } from "../composables/data";
import MediaCard from "../cards/MediaCard.vue";
import DialogCard from "../cards/DialogCard.vue";
import VLessonPlaceholder from "../VLessonPlaceholder.vue";
import { ILayoutComponentData } from "../types/ILayoutFieldData";
import { 
  CssStyleHelpers,
  CssImageHelpers,
  CssVideoHelpers 
} from "../utils/cssStyleHelpers";
const props = defineProps({
  modelValue: {
    type: Object as PropType<ILayoutComponentData | undefined>,
    required: true,
  },
  mode: {
    type: String as PropType<"view" | "edit">,
    default: "view",
  },
});

// Використовуємо дані напряму з newData
const applyDefaultMainPaddings = (paddings?: Record<string, number>) => ({
  top: paddings?.top ?? 0,
  right: paddings?.right ?? 0,
  bottom: paddings?.bottom ?? 0,
  left: paddings?.left ?? 0,
  ...paddings,
});

const applyDefaultMainMargins = (margins?: Record<string, number>) => ({
  top: margins?.top ?? 50,
  right: margins?.right ?? 0,
  bottom: margins?.bottom ?? 0,
  left: margins?.left ?? 0,
  ...margins,
});

const fields = computed(() => {
  const newData = props.modelValue?.data;
  if (!newData) return { mainGroup: {}, cardsGroup: {} };

  const { getData } = useDataFields(newData);
  const mediaBackground = getData('mainGroup.mediaBackground') || getData('mainGroup.media');

  return {
    mainGroup: {
      isShowMedia: getData('mainGroup.isShowMedia')?.value,
      isShowColor: getData('mainGroup.isShowColor')?.value,
      mediaMask: getData('mainGroup.mediaMask'),
      backgroundColor: getData('mainGroup.backgroundColor')?.value,
      borderRadius: getData('mainGroup.borderRadius')?.value,
      mediaBackground,
      // Використовуємо хелпери для коректної обробки всіх налаштувань
      mediaBackgroundType: mediaBackground?.type,
      objectFit: mediaBackground?.objectFit ?? 'cover',
      imageProps: CssImageHelpers.imageProps(mediaBackground),
      videoProps: {
        ...CssVideoHelpers.videoProps(mediaBackground),
        autoplay: mediaBackground?.autoplay,
        muted: mediaBackground?.muted,
        loop: mediaBackground?.loop,
      },
      maskProps: getData('mainGroup.mediaMask'),
      paddings: applyDefaultMainPaddings(getData('mainGroup.paddings')),
      paddingsMobile: applyDefaultMainPaddings(
        getData('mainGroup.paddingsMobile') || getData('mainGroup.paddings')
      ),
      margins: applyDefaultMainMargins(getData('mainGroup.margins')),
    },
    // Все данные и стили для карточек
    cardsGroup: {
      // Основные данные карточек
      cardsCollection: getData('cardsGroup.cards')?.cardsCollection,

      // Стили карточек
      cardColor: getData('cardsGroup.cardColor')?.value,
      cardBorderRadius: getData('cardsGroup.cardBorderRadius')?.value,
      mediaBorderRadius: getData('cardsGroup.mediaBorderRadius')?.value,
      mediaSize: getData('cardsGroup.mediaSize')?.value,
      mediaAlign: getData('cardsGroup.mediaAlign')?.value,
      maskProps: getData('cardsGroup.mediaMask'),
      showMedia: true,
      mediaGap: getData('cardsGroup.mediaGap')?.value,

      // Поля розмітки тепер в cardsGroup
      cardInnerSpacing: getData('cardsGroup.cardInnerSpacing')?.value,
      cardTextPaddings: applyDefaultMainPaddings(getData('cardsGroup.cardTextPaddings')),

      // Стили контента карточек (внутренний фон)
      contentBackgroundColor: getData('cardsGroup.cardColor')?.value,
      isContentBackgroundShown: getData('cardsGroup.isShowColor')?.value,

      // Настройки текста
      showText: true,
      textColor: '#000000',
    },
  };
});

const isEditMode = computed(() => props.mode === "edit");

// Перевіряємо чи блок пустий
const isEmptyBlock = computed(() => {
  const hasCards = fields.value?.cardsGroup?.cardsCollection && fields.value.cardsGroup.cardsCollection.length > 0;
  const hasMedia = fields.value?.mainGroup?.isShowMedia && (
    fields.value?.mainGroup?.imageProps?.imageSrc || 
    fields.value?.mainGroup?.videoProps?.videoSrc
  );
  const hasColor = fields.value?.mainGroup?.isShowColor && fields.value?.mainGroup?.backgroundColor;
  
  return !(hasCards || hasMedia || hasColor);
});
</script>
<style scoped>
@reference "tailwindcss";
.lesson-text-box {
  position: relative;
}

.lesson-debug-mode::after {
  content: "";
  position: absolute;
  inset: 0;
  border: 1px dashed rgba(59, 130, 246, 0.4);
  pointer-events: none;
  border-radius: inherit;
}

/* Основные стили для контейнера */
.lesson-text-box {
  @apply w-full flex flex-col gap-0;
  container-type: inline-size;
}

/* Стили для заголовка */
.lesson-title {
  @apply mb-4 relative z-10;
  color: var(--text-primary);
  /* background: rgba(255, 255, 255, 0.9); */
  /* padding: 0.75rem 1rem; */
  border-radius: 0.5rem;
  /* backdrop-filter: blur(2px); */
  display: inline-block;
}

/* Стили для текста */
.lesson-text {
  @apply text-base leading-relaxed m-0 p-0 mb-6;
  color: var(--text-primary);
}

/* Стили для медиа фона */
.media-background {
  @apply w-full h-full absolute inset-0 z-0;
  border-radius: inherit;
  overflow: hidden;
}
.media-background :deep(.media-card) {
  height: 100%;
}

/* Override @container height:auto !important from MediaContainerLayer and MaskLayer —
   those resets are correct for inline media but must not apply to the background fill. */
.media-background :deep(.media-container-layer),
.media-background :deep(.media-container-layer > div) {
  height: 100% !important;
  min-height: unset !important;
  max-height: unset !important;
  aspect-ratio: unset !important;
}

.media-background :deep(img),
.media-background :deep(video) {
  @apply w-full h-full object-cover;
}

/* Стили для текста поверх медиа */
.card-item {
  @apply text-base leading-relaxed relative z-10;
  /* color: var(--text-primary); */
  /* background: rgba(255, 255, 255, 0.2); */
}

/* Стили для заглушки */


.placeholder-icon {
  @apply text-5xl text-gray-500 mb-4 opacity-70;
}

.placeholder-text h3 {
  @apply text-lg font-semibold text-gray-700 m-0 mb-2;
}

.placeholder-text p {
  @apply text-sm text-gray-500 m-0 leading-relaxed;
}

/* Адаптивность для мобильных устройств */
@container (max-width: 700px) {
  .main-group {
    padding-top: var(--main-padding-top-mobile) !important;
    padding-right: var(--main-padding-right-mobile) !important;
    padding-bottom: var(--main-padding-bottom-mobile) !important;
    padding-left: var(--main-padding-left-mobile) !important;
  }

  .lesson-text {
    @apply text-base;
  }

  .lesson-title {
    @apply text-base;
  }
}

</style>
<style>
@import "../styles/dialogs-styles.css";
</style>
